import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HelpCircle, Play, ChevronRight, ChevronLeft, Check, X, Clock, Trophy,
  Star, Zap, BarChart2, RefreshCw, BookOpen, ArrowLeft, Camera, Award,
  AlertCircle, Loader2, TrendingUp, Shield, Target, Flame, Timer
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell
} from 'recharts';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';
import api from '../../api';

// ─── Math Renderer ────────────────────────────────────────────────────────────
// Renders text containing LaTeX inline ($...$) and block ($$...$$) math
function MathText({ text, className = '' }) {
  if (!text) return null;
  // Split on $$...$$ (block) and $...$ (inline)
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Check for block math $$...$$
    const blockStart = remaining.indexOf('$$');
    const inlineStart = remaining.indexOf('$');

    if (blockStart !== -1 && blockStart <= inlineStart) {
      // Block math
      if (blockStart > 0) parts.push(<span key={key++}>{remaining.slice(0, blockStart)}</span>);
      const blockEnd = remaining.indexOf('$$', blockStart + 2);
      if (blockEnd === -1) { parts.push(<span key={key++}>{remaining}</span>); break; }
      const math = remaining.slice(blockStart + 2, blockEnd);
      try { parts.push(<BlockMath key={key++} math={math} />); }
      catch { parts.push(<span key={key++}>{`$$${math}$$`}</span>); }
      remaining = remaining.slice(blockEnd + 2);
    } else if (inlineStart !== -1) {
      // Inline math
      if (inlineStart > 0) parts.push(<span key={key++}>{remaining.slice(0, inlineStart)}</span>);
      const inlineEnd = remaining.indexOf('$', inlineStart + 1);
      if (inlineEnd === -1) { parts.push(<span key={key++}>{remaining}</span>); break; }
      const math = remaining.slice(inlineStart + 1, inlineEnd);
      try { parts.push(<InlineMath key={key++} math={math} />); }
      catch { parts.push(<span key={key++}>{`$${math}$`}</span>); }
      remaining = remaining.slice(inlineEnd + 1);
    } else {
      parts.push(<span key={key++}>{remaining}</span>);
      break;
    }
  }
  return <span className={className}>{parts}</span>;
}

function useTimer(initialSeconds, onExpire, running = true, resetKey = 0) {
  const [seconds, setSeconds] = useState(initialSeconds);
  const ref = useRef(null);
  // Reset when resetKey or initialSeconds changes
  useEffect(() => {
    setSeconds(initialSeconds);
  }, [initialSeconds, resetKey]);
  useEffect(() => {
    if (!running) { clearInterval(ref.current); return; }
    ref.current = setInterval(() => {
      setSeconds(s => {
        if (s <= 1) { clearInterval(ref.current); onExpire(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(ref.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, initialSeconds, resetKey]);
  return seconds;
}

function fmtTime(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}


// ─── Helpers ──────────────────────────────────────────────────────────────────
function pctColor(p) {
  if (p >= 80) return 'text-emerald-600';
  if (p >= 60) return 'text-blue-600';
  if (p >= 40) return 'text-amber-600';
  return 'text-red-600';
}
function pctBg(p) {
  if (p >= 80) return 'bg-emerald-500';
  if (p >= 60) return 'bg-blue-500';
  if (p >= 40) return 'bg-amber-500';
  return 'bg-red-500';
}

// ─── Lobby Screen ─────────────────────────────────────────────────────────────
function Lobby({ subjects, stats, competitions, moduleEnabled, onStart, onCompetition, onShowStats }) {
  const navigate = useNavigate();
  const [sel, setSel] = useState('');
  const [count, setCount] = useState(20);

  if (!moduleEnabled) return (
    <div className="max-w-lg mx-auto text-center py-20 px-4">
      <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <HelpCircle className="w-8 h-8 text-slate-400" />
      </div>
      <h2 className="text-xl font-bold text-slate-700 mb-2">Quiz Zone Not Available</h2>
      <p className="text-slate-500">Your school has not enabled the Quiz module yet.</p>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-black text-slate-800 flex items-center gap-2" data-testid="quiz-zone-title">
          <HelpCircle className="w-7 h-7 text-pink-500" /> Quiz Zone
        </h1>
        <p className="text-slate-500 mt-1">Test your knowledge, track your progress, and compete!</p>
      </div>

      {/* Stats row */}
      {stats && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Total Attempts', value: stats.total_attempts || 0, color: 'text-blue-600' },
            { label: 'Best Score', value: `${stats.best_score || 0}%`, color: pctColor(stats.best_score || 0) },
            { label: 'Trophies', value: (stats.trophies || []).length, color: 'text-amber-500' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-2xl p-3.5 shadow-sm border border-slate-100 text-center">
              <p className={`text-xl font-black ${s.color}`}>{s.value}</p>
              <p className="text-xs text-slate-400 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Start Quiz */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-5 pt-5 pb-4 border-b border-slate-50">
          <h3 className="font-bold text-slate-800 flex items-center gap-2">
            <Play className="w-4 h-4 text-blue-500" /> Practice Quiz
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">Questions are shuffled every session from your class pool</p>
        </div>
        <div className="p-5 flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">Subject</label>
            <select
              value={sel}
              onChange={e => setSel(e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-700 bg-white focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
              data-testid="quiz-subject-select"
            >
              <option value="">All Subjects</option>
              {subjects.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">Questions</label>
            <select
              value={count}
              onChange={e => setCount(Number(e.target.value))}
              className="border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-700 bg-white focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
              data-testid="quiz-count-select"
            >
              {[10, 20, 30, 40].map(n => <option key={n} value={n}>{n} Questions</option>)}
            </select>
          </div>
          <button
            onClick={() => onStart(sel, count)}
            className="flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d58d4] text-white font-bold px-6 py-2.5 rounded-xl shadow-sm transition-colors whitespace-nowrap"
            data-testid="quiz-start-btn"
          >
            <Play className="w-4 h-4" /> Start Quiz
          </button>
        </div>
      </div>

      {/* Competitions */}
      {competitions.length > 0 && (
        <div>
          <h3 className="font-bold text-slate-700 text-sm mb-3 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-500" /> Live Competitions
          </h3>
          <div className="space-y-3">
            {competitions.map(c => (
              <div
                key={c.id}
                className={`bg-white rounded-2xl border p-4 shadow-sm flex items-center gap-4 ${c.status === 'live' ? 'border-green-200 bg-green-50/30' : 'border-slate-100'}`}
                data-testid={`competition-${c.id}`}
              >
                <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${c.status === 'live' ? 'bg-green-100' : 'bg-blue-50'}`}>
                  <Trophy className={`w-5 h-5 ${c.status === 'live' ? 'text-green-600' : 'text-blue-500'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-800 text-sm">{c.title}</p>
                  <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                    <span className="text-xs text-slate-400">{c.subject}</span>
                    {c.requires_camera && (
                      <span className="flex items-center gap-1 text-xs text-amber-600"><Camera className="w-3 h-3" /> Camera required</span>
                    )}
                    {!c.is_free && (
                      <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">₦{(c.entry_fee || 0).toLocaleString()} entry</span>
                    )}
                    {c.is_free && <span className="text-xs font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">Free</span>}
                    {c.status === 'live' && <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full animate-pulse">LIVE</span>}
                  </div>
                </div>
                <button
                  onClick={() => onCompetition(c)}
                  disabled={c.registered && c.status !== 'live'}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-colors whitespace-nowrap ${
                    c.registered && c.status !== 'live'
                      ? 'bg-emerald-100 text-emerald-700'
                      : c.status === 'live' && c.registered
                      ? 'bg-green-500 hover:bg-green-600 text-white shadow-sm'
                      : 'bg-[#2563eb] hover:bg-[#1d58d4] text-white shadow-sm'
                  }`}
                  data-testid={`comp-action-${c.id}`}
                >
                  {c.registered && c.status !== 'live' ? 'Registered' : c.status === 'live' ? 'Play Now' : 'Register'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trophies */}
      {stats && (stats.trophies || []).length > 0 && (
        <div>
          <h3 className="font-bold text-slate-700 text-sm mb-3 flex items-center gap-2">
            <Award className="w-4 h-4 text-purple-500" /> My Trophies
          </h3>
          <div className="flex flex-wrap gap-2">
            {(stats.trophies || []).slice(0, 8).map(t => (
              <div key={t.id} className="flex items-center gap-2 bg-white border border-slate-100 rounded-2xl px-3.5 py-2.5 shadow-sm">
                <span className="text-lg">{t.trophy_emoji}</span>
                <div>
                  <p className="text-xs font-bold text-slate-700">{t.trophy_name}</p>
                  <p className="text-[10px] text-slate-400">{new Date(t.earned_at).toLocaleDateString('en-NG')}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick link to leaderboard */}
      <div className="flex justify-center gap-4">
        <button
          onClick={() => navigate('/student/leaderboard')}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 font-medium transition-colors"
        >
          <BarChart2 className="w-4 h-4" /> View Leaderboard <ChevronRight className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => onShowStats()}
          className="flex items-center gap-2 text-sm text-pink-500 hover:text-pink-700 font-medium transition-colors"
          data-testid="quiz-my-stats-btn"
        >
          <TrendingUp className="w-4 h-4" /> My Stats <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ─── Quiz Screen (dual timer, no mid-quiz answers) ────────────────────────────
function QuizScreen({ questions, subject, onFinish, timerConfig = {} }) {
  const total = questions.length;
  const cfg = timerConfig || {};
  const Q_SECS = (cfg.subject_timers || {})[subject] || cfg.default_timer || 75;
  const SESSION_SECS = total * Q_SECS + Math.floor(total * 0.2 * Q_SECS); // +20% buffer

  const [idx, setIdx]         = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [startTime]           = useState(Date.now());
  const [qKey, setQKey]       = useState(0); // resets per-question timer when idx changes

  const finishRef = useRef(false);

  const doFinish = useCallback(() => {
    if (finishRef.current || submitted) return;
    finishRef.current = true;
    setSubmitted(true);
    const timeSec = Math.round((Date.now() - startTime) / 1000);
    const qIds  = questions.map(q => q.id);
    const ansArr = questions.map((_, i) => answers[i] ?? -1);
    onFinish({ questions: qIds, answers: ansArr, subject, time_seconds: timeSec });
  }, [submitted, startTime, questions, answers, subject, onFinish]);

  // Session timer — auto-submit on expire
  const sessionLeft = useTimer(SESSION_SECS, doFinish, !submitted);

  // Per-question timer — auto-advance to next question on expire
  const qLeft = useTimer(Q_SECS, () => {
    if (submitted) return;
    if (idx < total - 1) { setIdx(i => i + 1); setQKey(k => k + 1); }
    else doFinish();
  }, !submitted, qKey);

  // Reset per-question timer when moving to a new question
  useEffect(() => { setQKey(k => k + 1); }, [idx]);

  const q = questions[idx];
  const answered = Object.keys(answers).length;
  const myAns = answers[idx];

  if (!q) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
    </div>
  );

  const pickAnswer = (optIdx) => {
    if (submitted) return;
    setAnswers(a => ({ ...a, [idx]: optIdx }));
  };

  const next = () => { if (idx < total - 1) { setIdx(i => i + 1); setQKey(k => k + 1); } };
  const prev = () => { if (idx > 0) { setIdx(i => i - 1); setQKey(k => k + 1); } };

  const sessionPct = Math.round((sessionLeft / SESSION_SECS) * 100);
  const qPct = Math.round((qLeft / Q_SECS) * 100);
  const sessionUrgent = sessionLeft <= 60;
  const qUrgent = qLeft <= 15;

  return (
    <div className="max-w-2xl mx-auto px-4 py-4 space-y-3">
      {/* ── Dual Timer Header ── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Session timer */}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Session</span>
              <span className={`text-xs font-black tabular-nums ${sessionUrgent ? 'text-red-600 animate-pulse' : 'text-slate-700'}`}>
                {fmtTime(sessionLeft)}
              </span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${sessionUrgent ? 'bg-red-500' : sessionPct > 50 ? 'bg-emerald-500' : 'bg-amber-500'}`}
                style={{ width: `${sessionPct}%` }}
              />
            </div>
          </div>

          {/* Question progress */}
          <div className="text-center px-3 border-x border-slate-100">
            <p className="text-xs text-slate-400">Q</p>
            <p className="text-base font-black text-slate-700">{idx + 1}<span className="text-slate-400 text-sm">/{total}</span></p>
          </div>

          {/* Per-question timer */}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">This Q ({Q_SECS}s)</span>
              <span className={`text-xs font-black tabular-nums ${qUrgent ? 'text-red-600 animate-pulse' : 'text-slate-700'}`}>
                {fmtTime(qLeft)}
              </span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${qUrgent ? 'bg-red-500' : qPct > 50 ? 'bg-blue-500' : 'bg-amber-500'}`}
                style={{ width: `${qPct}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Answered dots ── */}
      <div className="flex flex-wrap gap-1.5 justify-center">
        {questions.map((_, i) => (
          <button
            key={i}
            onClick={() => { setIdx(i); setQKey(k => k + 1); }}
            className={`w-6 h-6 rounded-full text-[10px] font-bold transition-all ${
              i === idx ? 'ring-2 ring-offset-1 ring-[#2563eb] bg-[#2563eb] text-white' :
              answers[i] !== undefined ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'
            }`}
          >
            {i + 1}
          </button>
        ))}
      </div>

      {/* ── Question Card ── */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
        {/* Context */}
        {q.exercise_context && (
          <div className="bg-blue-50 border-b border-blue-100 px-5 py-3.5">
            <p className="text-xs font-bold text-blue-600 uppercase tracking-wide mb-1">Read carefully</p>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{q.exercise_context}</p>
          </div>
        )}
        {/* Image */}
        {q.image_url && (
          <div className="px-5 pt-4">
            <img src={q.image_url} alt="question" className="rounded-xl max-h-48 object-contain w-full border border-slate-100" />
          </div>
        )}
        {/* Question — rich math rendering */}
        <div className="px-5 py-4">
          {subject === 'Mathematics' || q.question_type === 'latex' ? (
            <MathText text={q.question_text} className="font-semibold text-slate-800 text-base leading-snug" />
          ) : (
            <p className="font-semibold text-slate-800 text-base leading-snug">{q.question_text}</p>
          )}
        </div>
        {/* Options — NO answer reveal during quiz */}
        <div className="px-5 pb-5 space-y-2.5">
          {(q.options || []).map((opt, i) => {
            let style = 'border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50';
            if (myAns === i) style = 'border-[#2563eb] bg-blue-50 text-[#2563eb]';
            return (
              <button
                key={i}
                onClick={() => pickAnswer(i)}
                className={`w-full text-left flex items-center gap-3 px-4 py-3 rounded-2xl border-2 text-sm font-medium transition-all ${style}`}
                data-testid={`quiz-option-${i}`}
              >
                <span className="w-6 h-6 rounded-lg border border-current flex items-center justify-center flex-shrink-0 text-xs font-bold">
                  {String.fromCharCode(65 + i)}
                </span>
                {subject === 'Mathematics' || q.question_type === 'latex' ? (
                  <MathText text={opt} className="flex-1" />
                ) : (
                  <span className="flex-1">{opt}</span>
                )}
                {myAns === i && <Check className="w-4 h-4 text-[#2563eb] flex-shrink-0" />}
              </button>
            );
          })}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between px-5 pb-4 gap-3 border-t border-slate-50 pt-3">
          <button onClick={prev} disabled={idx === 0} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 disabled:opacity-30 transition-colors">
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>
          <span className="text-xs text-slate-400">{answered}/{total} answered</span>
          {idx < total - 1 ? (
            <button
              onClick={next}
              className="flex items-center gap-1.5 text-xs font-bold text-[#2563eb] hover:bg-blue-50 px-3 py-1.5 rounded-xl transition-colors"
              data-testid="quiz-next-btn"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={doFinish}
              disabled={answered === 0}
              className="flex items-center gap-1.5 text-xs font-bold bg-[#2563eb] hover:bg-[#1d58d4] text-white px-4 py-2 rounded-xl shadow-sm transition-colors disabled:opacity-50"
              data-testid="quiz-submit-btn"
            >
              Submit Quiz
            </button>
          )}
        </div>
      </div>

      {/* Finish early */}
      {answered >= Math.ceil(total / 2) && idx < total - 1 && (
        <div className="text-center">
          <button onClick={doFinish} className="text-xs text-slate-400 hover:text-slate-600 underline">
            Submit now ({answered}/{total} answered)
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Results Screen (with full review + exam readiness) ──────────────────────
function ResultsScreen({ result, questions, userAnswers, onRetry, onLeaderboard }) {
  const [readiness, setReadiness] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(null); // question index expanded
  const pct = result?.percentage || 0;
  const score = result?.score || 0;
  const total = result?.total || 0;
  const details = result?.details || [];

  useEffect(() => {
    api.get('/quiz/exam-readiness')
      .then(r => { if (r?.success) setReadiness(r.data); })
      .catch(() => {});
  }, []);

  const grade = pct >= 80 ? { label: 'Excellent!', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', ring: '#10b981' }
    : pct >= 60 ? { label: 'Good Job!', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', ring: '#2563eb' }
    : pct >= 40 ? { label: 'Keep Trying!', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', ring: '#f59e0b' }
    : { label: 'Need More Practice', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', ring: '#ef4444' };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-5" data-testid="quiz-results-screen">
      {/* ── Score ── */}
      <div className={`${grade.bg} ${grade.border} border-2 rounded-3xl p-6 shadow-sm text-center`}>
        <div className="relative w-24 h-24 mx-auto mb-3">
          <svg viewBox="0 0 36 36" className="w-24 h-24 -rotate-90">
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e2e8f0" strokeWidth="3" />
            <circle cx="18" cy="18" r="15.9" fill="none" stroke={grade.ring} strokeWidth="3"
              strokeDasharray={`${pct} ${100 - pct}`} strokeLinecap="round" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-2xl font-black ${grade.color}`}>{pct}%</span>
          </div>
        </div>
        <p className={`text-lg font-black ${grade.color}`}>{grade.label}</p>
        <p className="text-sm text-slate-500 mt-1">{score} / {total} correct</p>
        {result?.time_seconds > 0 && (
          <p className="text-xs text-slate-400 mt-1 flex items-center justify-center gap-1">
            <Timer className="w-3 h-3" /> {Math.floor(result.time_seconds / 60)}m {result.time_seconds % 60}s
          </p>
        )}
      </div>

      {/* ── XP + Perfect score ── */}
      <div className="flex gap-3 justify-center flex-wrap">
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-2.5">
          <Zap className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-bold text-amber-700">+{pct >= 50 ? 25 : 10} XP earned</span>
        </div>
        {pct === 100 && (
          <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-2xl px-5 py-2.5">
            <span className="text-base">🏆</span>
            <span className="text-sm font-bold text-purple-700">Perfect Score!</span>
          </div>
        )}
      </div>

      {/* ── Exam Readiness Panel ── */}
      {readiness && (
        <div className={`rounded-3xl border-2 p-5 ${
          readiness.readiness >= 75 ? 'bg-emerald-50 border-emerald-200' :
          readiness.readiness >= 60 ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'
        }`} data-testid="exam-readiness-panel">
          <div className="flex items-start gap-4">
            <div className="relative w-16 h-16 flex-shrink-0">
              <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e2e8f0" strokeWidth="3.5" />
                <circle cx="18" cy="18" r="15.9" fill="none"
                  stroke={readiness.readiness >= 75 ? '#10b981' : readiness.readiness >= 60 ? '#2563eb' : '#f59e0b'}
                  strokeWidth="3.5"
                  strokeDasharray={`${readiness.readiness} ${100 - readiness.readiness}`}
                  strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`text-sm font-black ${readiness.readiness >= 75 ? 'text-emerald-600' : readiness.readiness >= 60 ? 'text-[#2563eb]' : 'text-amber-600'}`}>
                  {readiness.readiness}%
                </span>
              </div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Shield className={`w-4 h-4 ${readiness.readiness >= 75 ? 'text-emerald-600' : readiness.readiness >= 60 ? 'text-[#2563eb]' : 'text-amber-600'}`} />
                <p className="font-black text-slate-800 text-sm">Exam Readiness</p>
                {readiness.trend !== 'unknown' && (
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    readiness.trend === 'improving' ? 'bg-emerald-100 text-emerald-700' :
                    readiness.trend === 'declining' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {readiness.trend === 'improving' ? '↑ Improving' : readiness.trend === 'declining' ? '↓ Declining' : '→ Stable'}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">{readiness.message}</p>
              <p className="text-[10px] text-slate-400 mt-1">{readiness.total} quiz{readiness.total !== 1 ? 'zes' : ''} completed · Avg {readiness.recent_avg}% (last 10)</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Full Question Review ── */}
      {details.length > 0 && (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden" data-testid="quiz-review">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <p className="font-black text-slate-800 text-sm">Question Review</p>
              <p className="text-xs text-slate-400 mt-0.5">{score} correct · {total - score} incorrect</p>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 bg-emerald-500 rounded-full" />
              <span className="text-[10px] text-slate-400">Correct</span>
              <div className="w-2 h-2 bg-red-400 rounded-full ml-2" />
              <span className="text-[10px] text-slate-400">Wrong</span>
            </div>
          </div>
          <div className="divide-y divide-slate-50">
            {details.map((d, i) => (
              <div key={i} className={`${reviewOpen === i ? 'bg-slate-50' : 'bg-white'}`}>
                <button
                  className="w-full flex items-start gap-3 px-5 py-3.5 text-left"
                  onClick={() => setReviewOpen(reviewOpen === i ? null : i)}
                  data-testid={`review-q-${i}`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-[10px] font-black ${d.is_correct ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-xs text-slate-700 leading-snug line-clamp-2">
                      <MathText text={d.question_text} />
                    </div>
                    {!d.is_correct && (
                      <p className="text-[10px] text-red-500 mt-0.5 flex items-center gap-1">
                        <X className="w-3 h-3" /> Your answer was wrong
                      </p>
                    )}
                  </div>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${d.is_correct ? 'bg-emerald-100' : 'bg-red-100'}`}>
                    {d.is_correct ? <Check className="w-3 h-3 text-emerald-600" /> : <X className="w-3 h-3 text-red-500" />}
                  </div>
                </button>
                {reviewOpen === i && (
                  <div className="px-5 pb-4 ml-9 space-y-2.5">
                    {/* Options */}
                    <div className="space-y-1.5">
                      {(d.options || []).map((opt, j) => {
                        const isCorrect = j === d.correct_index;
                        const isUserAns = j === d.user_answer;
                        let style = 'border-slate-100 bg-slate-50 text-slate-600';
                        if (isCorrect) style = 'border-emerald-300 bg-emerald-50 text-emerald-700 font-bold';
                        else if (isUserAns && !isCorrect) style = 'border-red-300 bg-red-50 text-red-700 line-through';
                        return (
                          <div key={j} className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs ${style}`}>
                            <span className="w-5 h-5 rounded-md border border-current flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                              {String.fromCharCode(65 + j)}
                            </span>
                            <MathText text={opt} className="flex-1" />
                            {isCorrect && <Check className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />}
                            {isUserAns && !isCorrect && <X className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />}
                          </div>
                        );
                      })}
                    </div>
                    {/* Explanation */}
                    {d.explanation && (
                      <div className="bg-blue-50 border border-blue-100 rounded-xl px-3 py-2.5">
                        <p className="text-[10px] font-bold text-blue-600 mb-0.5">Explanation</p>
                        <p className="text-xs text-blue-800 leading-relaxed">
                          <MathText text={d.explanation} />
                        </p>
                      </div>
                    )}
                    {d.tip && (
                      <div className="bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                        <p className="text-[10px] font-bold text-amber-600 mb-0.5">Tip</p>
                        <p className="text-xs text-amber-800">{d.tip}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Actions ── */}
      <div className="flex gap-3 justify-center">
        <button
          onClick={onRetry}
          className="flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d58d4] text-white font-bold px-6 py-2.5 rounded-xl shadow-sm transition-colors"
          data-testid="quiz-retry-btn"
        >
          <RefreshCw className="w-4 h-4" /> Practice Again
        </button>
        <button
          onClick={onLeaderboard}
          className="flex items-center gap-2 bg-white border border-slate-200 text-slate-700 font-semibold px-5 py-2.5 rounded-xl hover:bg-slate-50 transition-colors"
          data-testid="quiz-leaderboard-btn"
        >
          <Trophy className="w-4 h-4 text-amber-500" /> Leaderboard
        </button>
      </div>
    </div>
  );
}


// ─── Competition Modal ────────────────────────────────────────────────────────
function CompetitionModal({ comp, onClose, onRegistered }) {
  const [loading, setLoading] = useState(false);
  const [cameraOk, setCameraOk] = useState(!comp.requires_camera);

  const checkCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(t => t.stop());
      setCameraOk(true);
    } catch {
      toast.error('Camera access denied. This competition requires a camera.');
    }
  };

  const loadPaystackScript = () => new Promise(resolve => {
    if (window.PaystackPop) { resolve(true); return; }
    const s = document.createElement('script');
    s.src = 'https://js.paystack.co/v1/inline.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });

  const register = async () => {
    if (comp.requires_camera && !cameraOk) {
      toast.error('Please allow camera access first');
      return;
    }
    setLoading(true);
    try {
      if (comp.is_free) {
        const res = await api.post(`/quiz/competitions/${comp.id}/register`, {
          competition_id: comp.id,
          payment_reference: '',
        });
        if (res.success) { toast.success('Registered!'); onRegistered(); }
      } else {
        const initRes = await api.post(`/quiz/competitions/${comp.id}/init-payment`);
        if (!initRes.success) { toast.error(initRes.detail || 'Payment init failed'); setLoading(false); return; }
        const loaded = await loadPaystackScript();
        if (!loaded) { toast.error('Failed to load payment'); setLoading(false); return; }
        const pd = initRes.data;
        const handler = window.PaystackPop.setup({
          key: pd.public_key,
          reference: pd.reference,
          onSuccess: async (resp) => {
            const verRes = await api.get(`/quiz/competitions/verify-payment?reference=${resp.reference}`);
            if (verRes?.data?.status === 'paid') { toast.success('Registered!'); onRegistered(); }
            else toast.error('Payment verification failed');
          },
          onCancel: () => { toast.info('Payment cancelled'); setLoading(false); },
        });
        handler.openIframe();
        return;
      }
    } catch (err) {
      toast.error(err?.detail || 'Registration failed');
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="competition-modal">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 space-y-4">
        <div className="flex items-start justify-between">
          <h3 className="font-black text-slate-800 text-lg">{comp.title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-2 text-sm text-slate-600">
          <p><span className="font-semibold">Subject:</span> {comp.subject}</p>
          <p><span className="font-semibold">Questions:</span> {comp.question_count}</p>
          <p><span className="font-semibold">Duration:</span> {comp.duration_minutes} minutes</p>
          <p><span className="font-semibold">Date:</span> {new Date(comp.scheduled_at).toLocaleString('en-NG')}</p>
          <p><span className="font-semibold">Entry:</span> {comp.is_free ? 'Free' : `₦${(comp.entry_fee || 0).toLocaleString()}`}</p>
        </div>
        {comp.requires_camera && (
          <div className={`rounded-2xl p-3.5 border flex items-center gap-3 ${cameraOk ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
            <Camera className={`w-5 h-5 ${cameraOk ? 'text-emerald-600' : 'text-amber-600'}`} />
            <div className="flex-1">
              <p className={`text-xs font-bold ${cameraOk ? 'text-emerald-700' : 'text-amber-700'}`}>
                {cameraOk ? 'Camera ready' : 'Camera permission required'}
              </p>
              {!cameraOk && <p className="text-xs text-amber-600">This competition monitors participants via camera</p>}
            </div>
            {!cameraOk && (
              <button onClick={checkCamera} className="text-xs font-bold text-amber-700 bg-amber-100 px-3 py-1.5 rounded-xl">
                Allow
              </button>
            )}
          </div>
        )}
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button
            onClick={register}
            disabled={loading || (comp.requires_camera && !cameraOk)}
            className="flex-1 py-2.5 rounded-xl bg-[#2563eb] hover:bg-[#1d58d4] text-white text-sm font-bold shadow-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="comp-register-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : comp.is_free ? 'Register Free' : `Pay ₦${(comp.entry_fee || 0).toLocaleString()}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── My Stats Screen ─────────────────────────────────────────────────────────
function MyStatsScreen({ onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/quiz/my-performance')
      .then(r => { if (r?.success) setData(r.data); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <div className="animate-spin w-8 h-8 border-2 border-pink-500 border-t-transparent rounded-full" />
    </div>
  );

  const recent = (data?.recent || []).map((r, i) => ({ ...r, i }));
  const bySubject = data?.by_subject || [];

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
      {/* Back */}
      <button onClick={onBack} className="flex items-center gap-2 text-xs text-slate-400 hover:text-slate-600" data-testid="stats-back-btn">
        <ArrowLeft className="w-4 h-4" /> Back to Quiz Zone
      </button>

      <div>
        <h1 className="text-2xl font-black text-slate-800 flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-pink-500" /> My Quiz Performance
        </h1>
        <p className="text-slate-500 text-sm mt-0.5">Track your progress across subjects</p>
      </div>

      {/* Summary */}
      {data && (
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm text-center">
            <p className="text-2xl font-black text-[#2563eb]">{data.total_attempts}</p>
            <p className="text-xs text-slate-400 mt-0.5">Total Attempts</p>
          </div>
          <div className={`rounded-2xl p-4 border shadow-sm text-center ${
            data.overall_avg >= 70 ? 'bg-emerald-50 border-emerald-100' :
            data.overall_avg >= 50 ? 'bg-amber-50 border-amber-100' : 'bg-red-50 border-red-100'
          }`}>
            <p className={`text-2xl font-black ${
              data.overall_avg >= 70 ? 'text-emerald-600' : data.overall_avg >= 50 ? 'text-amber-600' : 'text-red-600'
            }`}>{data.overall_avg}%</p>
            <p className="text-xs text-slate-400 mt-0.5">Overall Average</p>
          </div>
        </div>
      )}

      {/* Score Trend Chart */}
      {recent.length >= 2 && (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5" data-testid="score-trend-chart">
          <h3 className="font-black text-slate-800 text-sm mb-1">Score Trend</h3>
          <p className="text-xs text-slate-400 mb-4">Your last {recent.length} quiz attempts</p>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={recent} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d?.slice(5) || ''} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} tickFormatter={v => `${v}%`} />
              <Tooltip
                formatter={(val, _, props) => [`${val}%`, props.payload?.subject || 'Score']}
                contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }}
              />
              <Line
                type="monotone" dataKey="pct" stroke="#2563eb" strokeWidth={2.5}
                dot={{ r: 4, fill: '#2563eb', strokeWidth: 0 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Per-Subject Bar Chart */}
      {bySubject.length > 0 && (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5" data-testid="subject-perf-chart">
          <h3 className="font-black text-slate-800 text-sm mb-1">Performance by Subject</h3>
          <p className="text-xs text-slate-400 mb-4">Average score across all attempts</p>
          <ResponsiveContainer width="100%" height={Math.max(160, bySubject.length * 42)}>
            <BarChart
              data={bySubject.map(s => ({ name: s.subject, avg: s.avg_pct, best: s.best_pct }))}
              layout="vertical"
              margin={{ top: 0, right: 20, left: 10, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} />
              <Tooltip
                formatter={(val, name) => [`${val}%`, name === 'avg' ? 'Average' : 'Best']}
                contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }}
              />
              <Bar dataKey="avg" radius={[0, 6, 6, 0]} maxBarSize={26} name="avg">
                {bySubject.map((s, i) => (
                  <Cell key={i} fill={s.avg_pct >= 70 ? '#10b981' : s.avg_pct >= 50 ? '#f59e0b' : '#ef4444'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="flex gap-4 mt-3 justify-center">
            {[['#10b981','≥70%'],['#f59e0b','50–69%'],['#ef4444','<50%']].map(([c,l]) => (
              <div key={l} className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: c }} />
                <span className="text-xs text-slate-400">{l}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Subject detail list */}
      {bySubject.length > 0 && (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-50">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">Subject Breakdown</p>
          </div>
          <div className="divide-y divide-slate-50">
            {bySubject.map((s, i) => (
              <div key={i} className="flex items-center gap-3 px-5 py-3" data-testid={`subject-stat-${i}`}>
                <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-4 h-4 text-[#2563eb]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-800 truncate">{s.subject}</p>
                  <p className="text-xs text-slate-400">{s.attempts} attempt{s.attempts !== 1 ? 's' : ''} · Best: {s.best_pct}%</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${s.avg_pct >= 70 ? 'bg-emerald-500' : s.avg_pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                      style={{ width: `${s.avg_pct}%` }}
                    />
                  </div>
                  <span className={`text-sm font-black w-12 text-right ${s.avg_pct >= 70 ? 'text-emerald-600' : s.avg_pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                    {s.avg_pct}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data && data.total_attempts === 0 && (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm py-16 text-center" data-testid="stats-empty">
          <TrendingUp className="w-10 h-10 text-slate-200 mx-auto mb-3" />
          <p className="font-bold text-slate-500">No quiz attempts yet</p>
          <p className="text-sm text-slate-400 mt-1">Complete quizzes to see your performance charts</p>
          <button onClick={onBack} className="mt-4 text-sm text-[#2563eb] font-semibold hover:underline">
            Start a Quiz
          </button>
        </div>
      )}
    </div>
  );
}


// ─── Main Page ────────────────────────────────────────────────────────────────
export default function QuizZonePage() {
  const navigate = useNavigate();
  const [screen, setScreen] = useState('lobby'); // lobby | quiz | results | stats
  const [subjects, setSubjects] = useState([]);
  const [timerConfig, setTimerConfig] = useState({ subject_timers: {}, default_timer: 75 });
  const [stats, setStats] = useState(null);
  const [competitions, setCompetitions] = useState([]);
  const [moduleEnabled, setModuleEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [quizSubject, setQuizSubject] = useState('');
  const [result, setResult] = useState(null);
  const [selectedComp, setSelectedComp] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [subRes, statsRes, compRes, timerRes] = await Promise.allSettled([
      api.get('/quiz/subjects'),
      api.get('/quiz/my-stats'),
      api.get('/quiz/competitions'),
      api.get('/quiz/timer-config'),
    ]);
    if (subRes.status === 'fulfilled') {
      if (subRes.value?.module_enabled === false) { setModuleEnabled(false); setLoading(false); return; }
      setSubjects(subRes.value?.data || []);
    }
    if (statsRes.status === 'fulfilled' && statsRes.value?.success) setStats(statsRes.value.data);
    if (compRes.status === 'fulfilled' && compRes.value?.success) setCompetitions(compRes.value.data || []);
    if (timerRes.status === 'fulfilled' && timerRes.value?.success)
      setTimerConfig(timerRes.value.data || { subject_timers: {}, default_timer: 75 });
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const startQuiz = async (subject, count) => {
    try {
      const url = `/quiz/start?count=${count}${subject ? `&subject=${encodeURIComponent(subject)}` : ''}`;
      const res = await api.get(url);
      if (res?.module_enabled === false) { setModuleEnabled(false); return; }
      if (!res?.success || !(res.data || []).length) { toast.error('No questions found for this subject/class'); return; }
      setQuestions(res.data);
      setQuizSubject(subject);
      setScreen('quiz');
    } catch { toast.error('Failed to load questions'); }
  };

  const handleFinish = async ({ questions: qIds, answers, subject, time_seconds }) => {
    try {
      const res = await api.post('/quiz/attempt', { questions: qIds, answers, subject, time_seconds });
      if (res?.success) { setResult(res.data); setScreen('results'); load(); }
    } catch { toast.error('Failed to submit quiz'); setScreen('lobby'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="animate-spin w-8 h-8 border-2 border-pink-500 border-t-transparent rounded-full" />
    </div>
  );

  return (
    <>
      {screen === 'lobby' && (
        <Lobby
          subjects={subjects}
          stats={stats}
          competitions={competitions}
          moduleEnabled={moduleEnabled}
          onStart={startQuiz}
          onCompetition={c => setSelectedComp(c)}
          onShowStats={() => setScreen('stats')}
        />
      )}
      {screen === 'quiz' && (
        <div>
          <div className="max-w-2xl mx-auto px-4 pt-4">
            <button onClick={() => setScreen('lobby')} className="flex items-center gap-2 text-xs text-slate-400 hover:text-slate-600 mb-3">
              <ArrowLeft className="w-4 h-4" /> Back to Quiz Zone
            </button>
          </div>
          <QuizScreen questions={questions} subject={quizSubject} onFinish={handleFinish} timerConfig={timerConfig} />
        </div>
      )}
      {screen === 'results' && (
        <ResultsScreen
          result={result}
          onRetry={() => { setScreen('lobby'); }}
          onLeaderboard={() => navigate('/student/leaderboard')}
        />
      )}
      {selectedComp && (
        <CompetitionModal
          comp={selectedComp}
          onClose={() => setSelectedComp(null)}
          onRegistered={() => { setSelectedComp(null); load(); }}
        />
      )}
      {screen === 'stats' && (
        <MyStatsScreen onBack={() => setScreen('lobby')} />
      )}
    </>
  );
}
